import { CSSProperties } from 'react';
import {
  CandidateJourneyStageStatus,
  CandidatePortalRoleQuery,
} from 'generated/graphql';

export type PortalRole = NonNullable<CandidatePortalRoleQuery['myRole']>;

/**
 * One portal journey tile. Progress/lock `status` is derived server-side
 * (CR-348) from the candidate's pipeline pointer and per-stage completion.
 */
export type JourneyTile = PortalRole['candidateJourney'][number];

/**
 * The tile "Your next step" points at (and the page scrolls to). The journey
 * can hold more than one UNLOCKED tile: the pipeline pointer's stage plus any
 * assessment the candidate holds an open invitation for (PDE-954). Prefer the
 * first one the candidate can actually open — an AI-only shortlist stage, for
 * instance, has no page — and fall back to the first UNLOCKED tile.
 */
export const selectUpNextTile = (
  tiles: JourneyTile[],
  getTileLink: (tile: JourneyTile) => string | undefined
): JourneyTile | undefined => {
  const currentTiles = tiles.filter(
    tile => tile.status === CandidateJourneyStageStatus.Unlocked
  );
  return (
    currentTiles.find(tile => getTileLink(tile) !== undefined) ??
    currentTiles[0]
  );
};

/** Branding colors are stored without a leading `#` — tolerate both. */
export const normalizeHexColor = (
  color: string | null | undefined
): string | undefined => {
  if (!color) {
    return undefined;
  }

  return color.startsWith('#') ? color : `#${color}`;
};

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Mix a hex colour toward white (positive `amount`) or black (negative).
 * Used to derive interactive states from the company's accent colour with
 * the same steps the design system uses for its own primary button:
 * hover is 20% toward white (#006aff → #3388ff), active 10% toward black
 * (#006aff → #005fe5).
 */
export const mixHexColor = (
  color: string | undefined,
  amount: number
): string | undefined => {
  if (!color || !HEX_COLOR.test(color)) {
    return undefined;
  }
  let hex = color.slice(1);
  if (hex.length === 3) {
    hex = [...hex].map(char => char + char).join('');
  }
  const target = amount >= 0 ? 255 : 0;
  const ratio = Math.abs(amount);
  const mixed = [0, 2, 4].map(offset => {
    const channel = parseInt(hex.slice(offset, offset + 2), 16);
    return Math.round(channel + (target - channel) * ratio)
      .toString(16)
      .padStart(2, '0');
  });
  return `#${mixed.join('')}`;
};

/**
 * Logo to show for a company in candidate-facing pages: the white-label
 * branding logo when one is set, otherwise the company's own logo.
 */
export const getCompanyLogoUrl = (company: {
  logoUrl?: string | null;
  branding?: { logoUrl?: string | null } | null;
}): string | undefined =>
  company.branding?.logoUrl || company.logoUrl || undefined;

export type PortalBrandingColors = {
  accentColor1?: string | null;
  accentColor2?: string | null;
  textColor?: string | null;
};

/**
 * Inline custom properties for the whitelabel tokens in `_portal-theme.scss`
 * (`--portal-accent-1/-2`, `--portal-text`). Set them on a page's root so
 * every branded element below picks up the company's colours; unset values
 * fall back to the design-system defaults.
 */
export const getPortalBrandingStyle = (
  branding: PortalBrandingColors | null | undefined
): CSSProperties => {
  const accent1 = normalizeHexColor(branding?.accentColor1);
  const accent1Hover = mixHexColor(accent1, 0.2);
  const accent1Active = mixHexColor(accent1, -0.1);
  return {
    '--portal-accent-1': accent1,
    '--portal-accent-1-hover': accent1Hover,
    '--portal-accent-1-active': accent1Active,
    '--portal-accent-2': normalizeHexColor(branding?.accentColor2),
    '--portal-text': normalizeHexColor(branding?.textColor),
    // Design-system button hooks (see ui-components button.module.scss):
    // every primary Button under a branded root recolours itself, including
    // its hover, pressed and loading states.
    '--button-primary-bg': accent1,
    '--button-primary-bg-hover': accent1Hover,
    '--button-primary-bg-active': accent1Active,
  } as CSSProperties;
};
